import { Optional, Provider, SkipSelf } from '@angular/core';

import { HttpLabelUsagePort } from '@protocols/infra/http';
import { APIListLabelUsageQuery } from '@protocols/use-cases';
// Type-only: the read surface is a plain interface with no ESM runtime export.
import type { IListLabelUsageQuery } from '@protocols/use-cases';

import {
  LabelConfigMultiAssignEffects,
  LIST_LABEL_USAGE_QUERY,
} from '../../../effects/label-config-multi-assign.effects';
import { LabelConfigMultiAssignState } from '../../../state/label-config-multi-assign.state';

import { LabelConfigMultiAssignFacade } from './label-config-multi-assign.facade';

/**
 * The session's own MVVM stack, without the read surface. For consumers that
 * host the session THEMSELVES and supply their own `LIST_LABEL_USAGE_QUERY`
 * (the inner editor's story and specs, which mount `LabelConfigEditorComponent`
 * or its facade directly, with no dialog around them). A consumer that opens the
 * real dialog gets the full bundle below by definition, and swaps the read from
 * an outer scope instead.
 */
export const labelConfigMultiAssignSessionProviders: Provider[] = [
  LabelConfigMultiAssignState,
  LabelConfigMultiAssignEffects,
  LabelConfigMultiAssignFacade,
];

/**
 * Full session providers of the multi-assign label-config dialog: the MVVM stack
 * plus the read surface, which reaches the `listLabelUsage` cloud function —
 * the read needs the master key, so it cannot run in the browser. Declared by
 * the dialog host so every open reads fresh and dies with the dialog; the shared
 * staged-config ledger (`LabelConfigState`) and the save cascade stay on the
 * opener's injector.
 */
export const labelConfigMultiAssignProviders: Provider[] = [
  ...labelConfigMultiAssignSessionProviders,
  // Prefer a read supplied by an OUTER injector (a stub in a story or a spec);
  // only when none exists does the dialog build the cloud-function-backed one.
  // `SkipSelf` skips the dialog component's own node injector, so the lookup
  // walks the chain the opener's `injector` establishes — the one handle a
  // consumer that opens the real dialog has, since `MatDialogConfig` carries no
  // `providers` field. Nothing in production provides the token, so the
  // fallthrough IS the production wiring, unchanged.
  //
  // The transport is constructed inline rather than behind a token of its own:
  // the read surface is the only injectable name here, and building it in the
  // fallthrough branch keeps it out of the scopes where a stub wins.
  {
    provide: LIST_LABEL_USAGE_QUERY,
    useFactory: (parent: IListLabelUsageQuery | null) => parent ?? new APIListLabelUsageQuery(new HttpLabelUsagePort()),
    deps: [[new Optional(), new SkipSelf(), LIST_LABEL_USAGE_QUERY]],
  },
];
